import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import EmptyState from '@/components/EmptyState'
import { get, set } from '@/lib/storage'
import { useMemo, useState } from 'react'
import { TBL } from '@/lib/keys'
import { logAudit } from '@/lib/audit'
import { get as getKV } from '@/lib/storage'

type Snapshot = {
  id:string; date:string; mill:string; job:string; product:string;
  rateLogging?:number; rateTrucking?:number; rateProduct?:number; notes?:string
}
const KEY = 'rate-history'

export default function RateHistory(){
  const [list, setList] = useState<Snapshot[]>(get<Snapshot[]>(KEY, []))
  const [q, setQ] = useState('')
  const [snap, setSnap] = useState<Snapshot>({
    id: crypto.randomUUID(), date: new Date().toISOString().slice(0,10),
    mill:'', job:'', product:'', rateLogging:undefined, rateTrucking:undefined, rateProduct:undefined, notes:''
  })

  const filtered = useMemo(()=>{
    const s = q.trim().toLowerCase()
    if (!s) return list
    return list.filter(x => JSON.stringify(x).toLowerCase().includes(s))
  }, [q, list])

  const cols: Col[] = [
    { key:'date', label:'Date' },
    { key:'mill', label:'Mill' },
    { key:'job', label:'Job' },
    { key:'product', label:'Product' },
    { key:'rl', label:'Log $/t' },
    { key:'rt', label:'Truck $/t' },
    { key:'rp', label:'Rate $/t' },
    { key:'notes', label:'Notes' },
  ]

  const rows = filtered.map(s => ({
    date: s.date,
    mill: s.mill,
    job: s.job,
    product: s.product,
    rl: s.rateLogging ?? '',
    rt: s.rateTrucking ?? '',
    rp: s.rateProduct ?? '',
    notes: s.notes || ''
  }))

  function save(){
    const next = [snap, ...list]
    setList(next); set(KEY, next)
    logAudit({ action:'rate-history-add', details:snap }, getKV('role','Admin'))
    setSnap({
      id: crypto.randomUUID(), date: new Date().toISOString().slice(0,10),
      mill:'', job:'', product:'', rateLogging:undefined, rateTrucking:undefined, rateProduct:undefined, notes:''
    })
  }
  function clearAll(){
    if (!confirm('Clear all snapshots?')) return
    setList([]); set(KEY, [])
  }

  return (
    <AppShell title="Rate History">
      <div className="card mb-3">
        <div className="card-body grid grid-cols-1 md:grid-cols-6 gap-2">
          <input className="input" type="date" value={snap.date} onChange={e=>setSnap({...snap, date:e.target.value})}/>
          <input className="input" placeholder="Mill" value={snap.mill} onChange={e=>setSnap({...snap, mill:e.target.value})}/>
          <input className="input" placeholder="Job" value={snap.job} onChange={e=>setSnap({...snap, job:e.target.value})}/>
          <input className="input" placeholder="Product" value={snap.product} onChange={e=>setSnap({...snap, product:e.target.value})}/>
          <input className="input" type="number" step="0.01" placeholder="Log $/t" value={snap.rateLogging ?? ''} onChange={e=>setSnap({...snap, rateLogging:Number(e.target.value)})}/>
          <input className="input" type="number" step="0.01" placeholder="Truck $/t" value={snap.rateTrucking ?? ''} onChange={e=>setSnap({...snap, rateTrucking:Number(e.target.value)})}/>
          <input className="input" type="number" step="0.01" placeholder="Rate $/t" value={snap.rateProduct ?? ''} onChange={e=>setSnap({...snap, rateProduct:Number(e.target.value)})}/>
          <input className="input md:col-span-3" placeholder="Notes" value={snap.notes||''} onChange={e=>setSnap({...snap, notes:e.target.value})}/>
          <div className="flex gap-2">
            <button className="btn" onClick={save}>Save</button>
            <button className="btn-secondary" onClick={clearAll}>Clear All</button>
            <input className="input" placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} />
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No rate snapshots">
          Save a rate snapshot above to begin building history.
        </EmptyState>
      ) : (
        <div className="card">
          <div className="card-header">Snapshots</div>
          <div className="card-body">
            <DataTable columns={cols} rows={rows} filename="rate-history.csv" storageKey={TBL.rateHistory} />
          </div>
        </div>
      )}
    </AppShell>
  )
}
